import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { RouteTransitionLogo } from '../components/RouteTransitionLogo';
import { completeLogin } from '../utils/postLoginRouting';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TwoFactorPage({ onLogin }) {
  const navigate = useNavigate();
  const location = useLocation();
  const challengeId = location.state?.challengeId || '';
  const emailLabel = location.state?.email || '';

  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [trustDevice, setTrustDevice] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const inputsRef = useRef([]);

  // If the user lands on /two-factor directly (page refresh, deep link)
  // we have nothing to verify against — kick them back to the login form.
  useEffect(() => {
    if (!challengeId) {
      toast.error('Your verification session expired. Please sign in again.');
      navigate('/login', { replace: true });
    }
  }, [challengeId, navigate]);

  const handleDigitChange = (index, value) => {
    const cleaned = value.replace(/\D/g, '').slice(-1);
    setDigits((prev) => {
      const next = [...prev];
      next[index] = cleaned;
      return next;
    });
    if (cleaned && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event) => {
    const pasted = (event.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    event.preventDefault();
    const next = ['', '', '', '', '', ''];
    for (let i = 0; i < pasted.length; i += 1) next[i] = pasted[i];
    setDigits(next);
    const focusIdx = Math.min(pasted.length, 5);
    inputsRef.current[focusIdx]?.focus();
  };

  const handleVerify = async (event) => {
    event.preventDefault();
    const code = digits.join('');
    if (code.length !== 6) {
      toast.error('Enter the 6-digit code from your email');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/2fa/verify`, {
        challenge_id: challengeId,
        code,
        trust_device: trustDevice,
      });
      await completeLogin({
        accessToken: res.data.access_token,
        user: res.data.user,
        onLogin,
        navigate,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'That code is invalid or has expired.');
      setDigits(['', '', '', '', '', '']);
      inputsRef.current[0]?.focus();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.45, ease: [0.25, 0.1, 0.25, 1] }}
      className="app-shell premium-page"
      data-testid="two-factor-page"
      style={{
        minHeight: '100vh',
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: 'var(--app-bg)',
        position: 'relative',
        paddingLeft: 24, paddingRight: 24, paddingBottom: 40,
      }}
    >
      <button
        onClick={() => navigate('/login', { replace: true })}
        aria-label="Back to login"
        data-testid="two-factor-back-button"
        style={{ position: 'absolute', top: 'calc(env(safe-area-inset-top, 0px) + 20px)', left: 20, background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
      >
        <ChevronLeft size={26} color="var(--text)" strokeWidth={1.75} />
      </button>

      <div className="auth-fade-up" style={{ animationDelay: '0ms' }}>
        <RouteTransitionLogo width={132} height={118} dataTestId="two-factor-shared-logo" style={{ marginTop: 56 }} />
      </div>

      <h1
        className="auth-fade-up"
        style={{
          fontFamily: "'Amarante', serif",
          fontSize: 28,
          fontWeight: 400,
          color: 'var(--text)',
          textAlign: 'center',
          marginTop: 14,
          marginBottom: 8,
          animationDelay: '80ms',
        }}
      >
        Verify it’s you
      </h1>

      <p
        className="auth-fade-up"
        style={{
          fontFamily: "'Outfit', sans-serif",
          fontSize: 14,
          lineHeight: 1.5,
          color: 'var(--text-2)',
          textAlign: 'center',
          maxWidth: 320,
          margin: '0 0 24px',
          animationDelay: '140ms',
        }}
        data-testid="two-factor-instructions"
      >
        We sent a 6-digit code to{' '}
        <span style={{ color: 'var(--text)', fontWeight: 600 }} data-testid="two-factor-email-label">
          {emailLabel || 'your email'}
        </span>
        . Enter it below to finish signing in.
      </p>

      <form
        id="two-factor-form"
        onSubmit={handleVerify}
        className="premium-card auth-fade-up"
        style={{ width: '100%', maxWidth: 360, padding: '24px', animationDelay: '200ms' }}
        onPaste={handlePaste}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          {digits.map((digit, index) => (
            <input
              key={index}
              ref={(el) => { inputsRef.current[index] = el; }}
              type="tel"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={1}
              value={digit}
              onChange={(e) => handleDigitChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              data-testid={`two-factor-digit-${index}`}
              style={{
                width: 44,
                height: 56,
                textAlign: 'center',
                fontSize: 24,
                fontFamily: "'Outfit', sans-serif",
                fontWeight: 600,
                color: 'var(--text)',
                border: '1px solid var(--card-border)',
                borderRadius: 12,
                outline: 'none',
                background: 'var(--surface)',
              }}
            />
          ))}
        </div>

        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginTop: 22,
            fontFamily: "'Outfit', sans-serif",
            fontSize: 14,
            color: '#444',
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={trustDevice}
            onChange={(e) => setTrustDevice(e.target.checked)}
            data-testid="two-factor-trust-checkbox"
            style={{ width: 18, height: 18, accentColor: '#7C3AED' }}
          />
          Trust this device for 90 days
        </label>
      </form>

      <motion.button
        type="submit"
        form="two-factor-form"
        disabled={submitting}
        whileHover={submitting ? {} : { scale: 1.01 }}
        whileTap={submitting ? {} : { scale: 0.99 }}
        className="auth-primary-btn auth-fade-up"
        data-testid="two-factor-verify-button"
        style={{ maxWidth: 360, marginTop: 24, animationDelay: '260ms' }}
      >
        {submitting ? 'Verifying…' : 'Verify & Sign In'}
      </motion.button>

      <p
        className="auth-fade-up"
        style={{
          fontFamily: "'Outfit', sans-serif",
          fontSize: 13,
          color: '#888',
          marginTop: 22,
          textAlign: 'center',
          maxWidth: 320,
          lineHeight: 1.6,
          animationDelay: '320ms',
        }}
      >
        Didn’t get a code? Check your spam folder, or{' '}
        <button
          type="button"
          onClick={() => navigate('/login', { replace: true })}
          className="auth-link"
          data-testid="two-factor-retry-button"
          style={{ background: 'none', border: 'none', color: '#7C3AED', cursor: 'pointer', padding: 0, fontWeight: 600 }}
        >
          try signing in again
        </button>
        .
      </p>
    </motion.div>
  );
}
